/**
 * @file template-rules.ts
 * @description Правила шаблона страницы товара в кабинете без отрисовки
 * (phase19.16 Р2а; uiux-design-r2a §3, §6; SB34; db-design-r2a, ответ 2а):
 *  - контекст секции по виду страницы (обычная, место, шаблон);
 *  - где в шаблоне уже стоит элемент данных товара (меню «⊕ элемент»:
 *    «Уже есть в блоке «…» [Показать]»);
 *  - обязательные элементы блока и куда их перенести при удалении блока;
 *  - сводка элементов блока для структуры («Галерея · Цена и покупка (з)»).
 * Проверку целиком делает `checkProductTemplate` из shared (сервер — тем же).
 */

import {
  checkProductTemplate,
  isProductElementKind,
  isProductRequiredKind,
  type ProductElementKind,
  type SectionContext,
  type SectionElement,
  type SectionPayload,
  type TemplateIssue,
} from "@chaiberi/shared";

import { blockTitle, isSectionBlock } from "../../components/site-pages/block-meta";
import type { SitePageBlock, SitePageKind } from "../api-site-pages";
import { elementCount } from "./section-edit";
import { ELEMENT_KIND_META } from "./section-meta";

/** Контекст элементов секции по виду страницы (shared contexts.ts). */
export function sectionContextOf(kind: SitePageKind | string | null | undefined): SectionContext {
  if (kind === "TEMPLATE") return "productTemplate";
  if (kind === "SLOT") return "slot";
  return "page";
}

/**
 * Тексты действий с блоком по контексту: все блоки — в черновике до
 * «Опубликовать» (шаблон товара — SB61, страницы и места — Р3а, SB126).
 */
export const BLOCK_ACTION_TEXT: Readonly<Record<SectionContext, { hidden: string; removeBody: string }>> = {
  // Р3а (SB126): блоки страниц и мест — тоже в черновике; на сайте — после «Опубликовать».
  page: { hidden: "скрыт в черновике; на сайте — после «Опубликовать»", removeBody: "Блок уберётся из черновика страницы; на сайте он останется до «Опубликовать». Вернуть можно кнопкой «Отменить»." },
  slot: { hidden: "скрыт в черновике; на сайте — после «Опубликовать»", removeBody: "Блок уберётся из черновика места; на сайте он останется до «Опубликовать». Вернуть можно кнопкой «Отменить»." },
  productTemplate: {
    hidden: "в черновике шаблона; на сайте — после «Опубликовать»",
    removeBody: "Блок уберётся из черновика шаблона; на сайте прежний шаблон — до «Опубликовать». Вернуть можно кнопкой «Отменить».",
  },
};

/** Где стоит элемент данных товара: блок (название) и id элемента. */
export interface ElementRef {
  blockId: string;
  blockTitle: string;
  elementId: string;
}

/** Блок шаблона с payload: строка страницы или правимая форма. */
interface TemplateBlock {
  id: string;
  title: string;
  payload: SectionPayload;
}

function sectionBlocks(blocks: readonly SitePageBlock[]): TemplateBlock[] {
  return blocks.filter(isSectionBlock).map((b) => ({ id: b.id, title: blockTitle(b), payload: b.payload as SectionPayload }));
}

/**
 * Где уже стоят элементы данных товара. `current` — правимый блок с
 * несохранённым payload (он заменяет сохранённую строку того же id).
 */
export function productElementIndex(blocks: readonly SitePageBlock[], current: TemplateBlock | null): Partial<Record<ProductElementKind, ElementRef>> {
  const list = sectionBlocks(blocks).map((b) => (current && b.id === current.id ? current : b));
  if (current && !list.some((b) => b.id === current.id)) list.push(current);
  const index: Partial<Record<ProductElementKind, ElementRef>> = {};
  for (const block of list) {
    for (const column of block.payload.columns.items) {
      for (const el of column.elements) {
        if (isProductElementKind(el.kind) && !index[el.kind]) index[el.kind] = { blockId: block.id, blockTitle: block.title, elementId: el.id };
      }
    }
  }
  return index;
}

/** Обязательные элементы секции (по порядку колонок). */
export function requiredIn(payload: SectionPayload): SectionElement[] {
  return payload.columns.items.flatMap((c) => c.elements.filter((el) => isProductRequiredKind(el.kind)));
}

/** В блоке есть обязательные элементы (скрыть нельзя, удалить — только с переносом). */
export function blockHasRequired(block: SitePageBlock): boolean {
  return isSectionBlock(block) && requiredIn(block.payload as SectionPayload).length > 0;
}

/** Колонка блока, куда переносить: та, где «Цена и покупка», иначе последняя. */
export function targetColumn(payload: SectionPayload): number {
  const buy = payload.columns.items.findIndex((c) => c.elements.some((el) => el.kind === "product-buy"));
  return buy >= 0 ? buy : Math.max(0, payload.columns.items.length - 1);
}

export interface RemovePlan {
  /** Обязательные элементы удаляемого блока. */
  required: SectionElement[];
  /** Куда перенести: блок с «Ценой и покупкой», иначе первый другой; `null` — некуда. */
  target: SitePageBlock | null;
  /** Сколько в блоке необязательных элементов — удалятся вместе с ним. */
  rest: number;
}

/** Что сделать перед удалением блока шаблона с обязательными элементами (uiux-design-r2a §6). */
export function removePlan(blocks: readonly SitePageBlock[], block: SitePageBlock): RemovePlan {
  const required = isSectionBlock(block) ? requiredIn(block.payload as SectionPayload) : [];
  const total = isSectionBlock(block) ? elementCount(block.payload as SectionPayload) : 0;
  const others = blocks.filter((b) => b.id !== block.id && isSectionBlock(b));
  const main = others.find((b) => (b.payload as SectionPayload).columns.items.some((c) => c.elements.some((el) => el.kind === "product-buy")));
  return { required, target: main ?? others[0] ?? null, rest: total - required.length };
}

/** «Название» и «Цена и покупка» — перечисление названий для текста окна. */
export function labelsList(elements: readonly SectionElement[]): string {
  const labels = elements.map((el) => `«${ELEMENT_KIND_META[el.kind].label}»`);
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} и ${labels[labels.length - 1]}`;
}

/** Текст окна «Перенести и удалить блок» (uiux-design-r2a §6). */
export function removeBlockText(plan: RemovePlan): string {
  // «Сведения о товаре» — множественное число: у одного элемента нейтральное «Этот элемент».
  const verb = plan.required.length > 1 ? "Их нельзя удалить" : "Этот элемент нельзя удалить";
  const target = plan.target ? blockTitle(plan.target) : "";
  const text = `В блоке есть ${labelsList(plan.required)}. ${verb} — перенесём в блок «${target}».`;
  // Необязательные (в т. ч. «Продавец») не переносятся — говорим об этом прямо, а не удаляем молча.
  return plan.rest > 0 ? `${text} Остальные элементы удалятся вместе с блоком.` : text;
}

/** Замечания `checkProductTemplate` по блокам шаблона (неподдерживаемые блоки не считаются). */
export function templateIssues(blocks: readonly SitePageBlock[]): TemplateIssue[] {
  return checkProductTemplate(blocks.filter(isSectionBlock).map((b) => ({ isEnabled: b.isEnabled, payload: b.payload as SectionPayload })));
}

/** Сводка элементов блока шаблона для структуры: «Галерея · Название (з) · Цена и покупка (з)». */
export function templateElementLines(payload: SectionPayload): Array<{ id: string; label: string; required: boolean }> {
  return payload.columns.items.flatMap((c) =>
    c.elements.map((el) => ({
      id: el.id,
      label: ELEMENT_KIND_META[el.kind].label,
      required: isProductRequiredKind(el.kind),
    })),
  );
}
